import React from 'react'
import PropTypes from 'prop-types'
import { compose, isEmpty } from 'ramda'
import { Button as AntdButton } from 'antd'

import { classNames } from 'utils/utils'

import withStyle, { StyledIcon } from './Button.style.js'

const Button = React.forwardRef(
  (
    {
      className,
      disabled,
      text,
      onClick,
      type,
      children,
      size,
      loading,
      style,
      link,
      iconProps,
      dataTestId,
      title,
      'aria-label': ariaLabel,
    },
    ref
  ) => {
    if (link) {
      return (
        <button
          type="button"
          className={classNames('chec-button', className)}
          disabled={disabled}
          onClick={onClick}
          style={style}
          loading={loading}
          ref={ref}
          data-test-id={dataTestId}
          title={title}
          aria-label={ariaLabel}
        >
          {!isEmpty(iconProps) && !iconProps.trailing && (
            <StyledIcon {...iconProps} />
          )}
          {text || children}
          {!isEmpty(iconProps) && iconProps.trailing && (
            <StyledIcon {...iconProps} />
          )}
        </button>
      )
    }
    // Chrome and maybe others don't fire any leave-ish events reliably on
    // a disabled button, so the tooltip doesn't dimiss reliably, so we have
    // fake disabled.
    const disablableOnClick = (event) => {
      if (disabled) return
      if (event) onClick(event)
      else onClick()
    }

    return (
      <AntdButton
        className={classNames('chec-button', className)}
        onClick={disablableOnClick}
        type={type}
        style={style}
        size={antdSize(size)}
        loading={loading}
        aria-disabled={disabled}
        aria-label={ariaLabel}
        ref={ref}
        data-test-id={dataTestId}
      >
        {!isEmpty(iconProps) && <StyledIcon {...iconProps} />}
        {text || children}
      </AntdButton>
    )
  }
)

const antdSize = (size) => (size === 'medium' ? 'default' : size)

Button.defaultProps = {
  size: 'default',
  doMargins: true,
  type: 'primary',
  onColor: false,
  onClick: () => {},
  iconProps: {},
}

Button.propTypes = {
  /** CSS classname */
  className: PropTypes.string,
  /** Disable button */
  disabled: PropTypes.bool,
  /** Ant Design loading prop */
  loading: PropTypes.bool,
  /** Text of button - priority over children */
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** Child nodes of Button component */
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** OnClick function */
  onClick: PropTypes.func,
  /** Medium = default */
  size: PropTypes.oneOf(['large', 'default', 'medium', 'small']),
  /** Type of button */
  type: PropTypes.oneOf([
    'primary',
    'default',
    'secondary',
    'tertiary',
    'danger',
    'icon',
  ]),
  /** Custom button styles */
  style: PropTypes.object,
  /** When a button is on a color, it should either be light or dark text color */
  onColor: PropTypes.oneOf(['light', 'dark', false]),
  /** h/v margins per size */
  doMargins: PropTypes.bool,
  /** make it look like a link */
  link: PropTypes.bool,
  onContextMenu: PropTypes.func,
  onMouseDown: PropTypes.func,
  onTouchStart: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  iconProps: PropTypes.object, // see icon propTypes
  /** Native HTML title attribute — shown as browser tooltip on hover */
  title: PropTypes.string,
  /** Accessible label for the button */
  'aria-label': PropTypes.string,
  /** is the button round, used in styled component */
  round: PropTypes.bool,
  dataTestId: PropTypes.string,
}

export default compose(withStyle)(Button)
